import { watch, ref, Ref, onMounted, onBeforeUnmount, shallowRef, toRaw } from 'vue';
import * as echarts from 'echarts';
import type { EChartsOption } from 'echarts';
import { calculateWidth, uid } from '../../chart/utils';
import GeoJsTh from './geoTh.json';

interface Props {
  options: EChartsOption;
  height?: number | string;
  width?: number | string;
  rawData?: Charts.ChartsProps.RawData;
  colorList?: Charts.ChartsProps.ColorItem[];
  responsiveWidth?: boolean;
  responsiveHeight?: boolean;
  eventType?: string;
  function?: Function;
}

export default function (props: Props) {
  const chart = ref<HTMLDivElement | null>(null);
  const chartSize = shallowRef();
  const id = ref();
  id.value = uid();

  // เพื่อนบ้าน (เช่น legend ที่ auto-scale font) ตั้งขนาดตัวเองเสร็จช้ากว่า reflow แรกของ browser (ใช้ Vue watch + nextTick)
  // ResizeObserver ของกราฟเองอาจจับขนาดที่ยังไม่นิ่งตอน resize จอ ทำให้ canvas ค้างขนาดผิดจนกว่าจะ resize อีกครั้ง
  // จึงฟัง window resize เพิ่มเป็น safety net แล้ว resize() ซ้ำหลัง debounce ให้ layout ข้างเคียงนิ่งก่อน
  let windowResizeTimeout: ReturnType<typeof setTimeout> | undefined;
  const handleWindowResize = () => {
    clearTimeout(windowResizeTimeout);
    windowResizeTimeout = setTimeout(() => {
      chartSize.value?.resize();
    }, 300);
  };

  const init = () => {
    const dom = document.getElementById(String(id.value)) as HTMLElement;
    try {
      chartSize.value = echarts.init(dom);
      let initResize = false;

      if (chart.value) {
        const myChart = echarts.init(chart.value);
        chartSize.value = echarts.init(chart.value);
        myChart.showLoading();
        try {
          myChart.hideLoading();
          echarts.registerMap('Thailand', GeoJsTh as any, {});
          myChart.setOption(props.options);
        } catch (error) {
          console.error('Error loading Thailand geoJSON:', error);
          myChart.hideLoading();
        }
      }

      setTimeout(() => {
        initResize = true;
        chartSize.value.resize();
      }, 1000);

      new ResizeObserver(() => {
        if (initResize) chartSize.value.resize();
      }).observe(dom);

      if (props.eventType) {
        chartSize.value.on(props.eventType, props.function);
      }
    } catch (e) {
      //  TODO: this exception will happen when using with vue <transition>
    }
  };

  onMounted(() => {
    init();
    window.addEventListener('resize', handleWindowResize);
  });

  onBeforeUnmount(() => {
    window.removeEventListener('resize', handleWindowResize);
    clearTimeout(windowResizeTimeout);
  });

  watch(
    () => props.options,
    () => {
      if (chart.value) {
        const myChart = echarts.init(chart.value);
        myChart.setOption(props.options);
      }
    },
    {
      deep: true,
      immediate: true,
    },
  );

  watch([() => props.height, () => props.width], () => {
    setTimeout(() => {
      chartSize.value.resize();
    }, 100);
  });

  return { id, chart, calculateWidth };
}
